import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../../../themes/colors';
import { Button } from '../../Button';

export interface ConfirmationOverlayProps {
  title: string;
  subtitle?: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  cancelLabel: string;
  onCancel: () => void;
  /**
   * Visual treatment for the primary action.
   * - 'warning' (default): destructive (red/orange).
   * - 'active': non-destructive (dark/primary).
   */
  confirmState?: 'warning' | 'active';
  testID?: string;
}

export const ConfirmationOverlay: React.FC<ConfirmationOverlayProps> = ({
  title,
  subtitle,
  confirmLabel,
  onConfirm,
  cancelLabel,
  onCancel,
  confirmState = 'warning',
  testID,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <View style={styles.overlay} testID={testID}>
      <View style={styles.sheet}>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        <Button
          variant="rectangular"
          state={confirmState}
          style={styles.actionButton}
          onPress={handleConfirm}
          loading={isConfirming}
          disabled={isConfirming}
          size="large"
        >
          {confirmLabel}
        </Button>
        <Button
          variant="rectangular"
          style={styles.actionButton}
          onPress={onCancel}
          disabled={isConfirming}
          size="large"
        >
          {cancelLabel}
        </Button>
      </View>
    </View>
  );
};

ConfirmationOverlay.displayName = 'ConfirmationOverlay';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: Colors.blackOverlay,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.brand.white,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: Colors.grayText,
    textAlign: 'center',
    marginBottom: 20,
  },
  actionButton: {
    marginBottom: 8,
    width: '100%',
  },
});
